import { useEffect, useState } from "react";

export default function EnvSelector({ user, role, allowedEnvs, onSelect, onLogout }) {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [time, setTime] = useState(new Date());

  useEffect(()=>{ const id=setInterval(()=>setTime(new Date()),1000); return()=>clearInterval(id); },[]);

  // neumorphism light palette
  const BASE="#e0e5ec", SD="#a3b1c6", SL="#ffffff", TEXT="#4a5568", TSUB="#8b93a3";
  const raised=`6px 6px 12px ${SD}, -6px -6px 12px ${SL}`;
  const raisedSm=`5px 5px 10px ${SD}, -5px -5px 10px ${SL}`;
  const raisedLg=`9px 9px 18px ${SD}, -9px -9px 18px ${SL}`;
  const inset=`inset 4px 4px 8px ${SD}, inset -4px -4px 8px ${SL}`;

  const ICCC_LOCATIONS = [
    { key:"iccc-erode",  name:"Erode",  full:"ICCC Erode",  accent:"#e11d48", icon:"🏙", stats:[{label:"Servers",value:"2"},{label:"Docker",value:"1"},{label:"MySQL",value:"1"}] },
    { key:"iccc-trichy", name:"Trichy", full:"ICCC Trichy", accent:"#d97706", icon:"🏙", stats:[{label:"Servers",value:"2"},{label:"Docker",value:"1"},{label:"MySQL",value:"1"}] },
  ];

  const envs = [
    { key:"tnpdcl", name:"TNPDCL", full:"Tamil Nadu Power Distribution Corp.", icon:"⚡", accent:"#0284c7",
      stats:[{label:"Servers",value:"11"},{label:"DB Nodes",value:"3"},{label:"Services",value:"200+"},{label:"Containers",value:"65+"}] },
    { key:"fsa", name:"FSA", full:"FSA Environment", icon:"🏭", accent:"#0d9488",
      stats:[{label:"Servers",value:"7"},{label:"DB Nodes",value:"3"},{label:"Services",value:"50+"},{label:"Containers",value:"25+"}] },
    { key:"iccc", name:"ICCC", full:"Integrated Command & Control Centre", icon:"🏙", accent:"#e11d48",
      stats:[{label:"Servers",value:"4"},{label:"Locations",value:"2"},{label:"Services",value:"30+"},{label:"Containers",value:"40+"}] },
    { key:"inhouse", name:"In-House", full:"Internal Tools & Infrastructure", icon:"🏢", accent:"#7c3aed",
      stats:[{label:"Servers",value:"7"},{label:"Tools",value:"6"},{label:"Services",value:"20+"},{label:"Containers",value:"10+"}] },
  ].filter(e=>!allowedEnvs||allowedEnvs.includes(e.key));

  const handleClick = (env) => {
    if(env.key==="iccc"){ setExpanded(expanded==="iccc"?null:"iccc"); return; }
    setSelected(env.key);
    setTimeout(()=>onSelect(env.key), 600);
  };
  const handleLocation = (loc) => { setSelected(loc.key); setTimeout(()=>onSelect(loc.key), 600); };

  const Card = ({ item, isLoc }) => {
    const isHov = hovered===item.key, isSel = selected===item.key;
    return (
      <div onClick={()=>isLoc?handleLocation(item):handleClick(item)}
        onMouseEnter={()=>setHovered(item.key)} onMouseLeave={()=>setHovered(null)}
        style={{ position:"relative", background:BASE, borderRadius:22,
          padding:"clamp(20px,3vw,28px)", cursor:"pointer",
          boxShadow: isSel? inset : (isHov? raisedLg : raised),
          transform: isSel?"scale(.99)":isHov?"translateY(-4px)":"none",
          transition:"all .3s ease", overflow:"hidden" }}>
        {/* status */}
        <div style={{position:"absolute", top:18, right:18}}>
          <span style={{ fontSize:9, fontFamily:"'JetBrains Mono',monospace", fontWeight:700,
            padding:"4px 11px", borderRadius:20, letterSpacing:".08em", background:BASE,
            boxShadow:raisedSm, color:isSel?"#0d9488":item.accent }}>
            {isSel?"CONNECTING…":"OPERATIONAL"}
          </span>
        </div>
        {/* icon + name */}
        <div style={{display:"flex", alignItems:"center", gap:15, marginBottom:22}}>
          <div style={{ width:58, height:58, borderRadius:17, background:BASE, boxShadow:raisedSm,
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:27 }}>
            {isSel?(<div style={{width:20,height:20,borderRadius:"50%",border:`2px solid ${item.accent}`,borderTopColor:"transparent",animation:"espin .8s linear infinite"}}/>):item.icon}
          </div>
          <div>
            <div style={{fontSize:23, fontWeight:800, color:TEXT, lineHeight:1, marginBottom:5}}>{item.name}</div>
            <div style={{fontSize:12, color:TSUB, fontWeight:500}}>{item.full}</div>
          </div>
        </div>
        {/* stats */}
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:11, marginBottom:20}}>
          {item.stats.map(s=>(
            <div key={s.label} style={{ background:BASE, borderRadius:11, padding:"11px 13px", boxShadow:inset }}>
              <div style={{fontSize:9, color:TSUB, fontFamily:"'JetBrains Mono',monospace", letterSpacing:".08em", marginBottom:5}}>{s.label}</div>
              <div style={{fontSize:19, fontWeight:800, color:item.accent, fontFamily:"'JetBrains Mono',monospace"}}>{s.value}</div>
            </div>
          ))}
        </div>
        {/* enter */}
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <span style={{fontSize:12, color:isHov?item.accent:TSUB, fontWeight:600, transition:"color .3s"}}>
            {isSel?"Initializing…":(item.key==="iccc"?"Click to choose location":"Click to enter")}
          </span>
          <div style={{ width:34, height:34, borderRadius:"50%", background:BASE, boxShadow:isHov?inset:raisedSm,
            display:"flex", alignItems:"center", justifyContent:"center", color:item.accent, fontSize:15,
            fontWeight:700, transition:"all .3s", transform:isHov?"translateX(3px)":"none" }}>→</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight:"100vh", background:BASE, display:"flex", flexDirection:"column",
      alignItems:"center", fontFamily:"'Space Grotesk',-apple-system,sans-serif",
      overflowX:"hidden", overflowY:"auto", paddingTop:"clamp(80px,10vh,120px)", paddingBottom:48, boxSizing:"border-box" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600;700&display=swap');
        @keyframes efadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
        @keyframes espin{to{transform:rotate(360deg)}}
        @keyframes efloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
      `}</style>

      {/* Top bar */}
      <div style={{position:"fixed", top:0, left:0, right:0, zIndex:10,
        padding:"12px clamp(14px,3vw,32px)", display:"flex", alignItems:"center", justifyContent:"space-between",
        flexWrap:"wrap", gap:8, background:BASE, boxShadow:`0 4px 12px ${SD}`}}>
        <div style={{display:"flex", alignItems:"center", gap:12}}>
          <span style={{fontFamily:"'JetBrains Mono',monospace", fontSize:14, color:TEXT, fontWeight:700, letterSpacing:".06em"}}>
            BN<span style={{color:"#0284c7"}}>360</span>
          </span>
          <span style={{fontSize:10, color:TSUB, fontFamily:"'JetBrains Mono',monospace", background:BASE, padding:"3px 8px", borderRadius:6, boxShadow:inset}}>AI · v2.0</span>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:16}}>
          <span style={{fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:TSUB}}>{time.toLocaleTimeString("en-IN",{hour12:false})}</span>
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <div style={{width:30, height:30, borderRadius:"50%", background:BASE, boxShadow:raisedSm,
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#0284c7"}}>
              {user?.[0]?.toUpperCase()}
            </div>
            <span style={{fontSize:12, color:TEXT, fontWeight:600}}>{user}</span>
            {role==="readonly"&&(<span style={{fontSize:9, padding:"3px 9px", borderRadius:20, background:BASE, boxShadow:raisedSm, color:"#d97706", fontFamily:"'JetBrains Mono',monospace", fontWeight:700}}>VIEW ONLY</span>)}
          </div>
          <button onClick={onLogout} style={{ background:BASE, border:"none", color:TSUB, borderRadius:9,
            padding:"7px 15px", cursor:"pointer", fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:600, boxShadow:raisedSm }}>Logout</button>
        </div>
      </div>

      <div style={{position:"relative", zIndex:5, width:"100%", maxWidth:1140, padding:"0 24px", animation:"efadeUp .6s ease both"}}>
        {!expanded && (
          <>
            <div style={{textAlign:"center", marginBottom:38}}>
              <div style={{fontSize:11, fontFamily:"'JetBrains Mono',monospace", color:"#0284c7", letterSpacing:".18em", marginBottom:14, fontWeight:700}}>
                ⚡ AI-POWERED · ENVIRONMENT SELECTION
              </div>
              <h1 style={{fontSize:"clamp(28px,5vw,44px)", fontWeight:800, color:TEXT, lineHeight:1.15, margin:"0 0 12px"}}>
                Select Your <span style={{color:"#0284c7"}}>Environment</span>
              </h1>
              <p style={{fontSize:15, color:TSUB, fontWeight:500, margin:0}}>Choose the infrastructure environment to monitor and manage</p>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,290px),1fr))", gap:22}}>
              {envs.map((env,i)=>(<div key={env.key} style={{animation:`efadeUp .6s ease ${i*.12}s both`}}><Card item={env} isLoc={false}/></div>))}
            </div>
          </>
        )}

        {expanded==="iccc" && (
          <div style={{animation:"efadeUp .4s ease both"}}>
            <button onClick={()=>{setExpanded(null);setHovered(null);}} style={{ background:BASE, border:"none", color:TSUB,
              borderRadius:10, padding:"8px 18px", cursor:"pointer", fontFamily:"'Space Grotesk',sans-serif",
              fontSize:13, fontWeight:600, marginBottom:28, boxShadow:raisedSm }}>← Back to environments</button>
            <div style={{textAlign:"center", marginBottom:32}}>
              <div style={{fontSize:11, fontFamily:"'JetBrains Mono',monospace", color:"#e11d48", letterSpacing:".18em", marginBottom:12, fontWeight:700}}>ICCC LOCATION</div>
              <h1 style={{fontSize:"clamp(26px,5vw,40px)", fontWeight:800, color:TEXT, lineHeight:1.15, margin:"0 0 10px"}}>Select <span style={{color:"#e11d48"}}>Location</span></h1>
              <p style={{fontSize:15, color:TSUB, margin:0}}>Choose the ICCC location to monitor</p>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,260px),1fr))", gap:20, maxWidth:680, margin:"0 auto"}}>
              {ICCC_LOCATIONS.map((loc,i)=>(<div key={loc.key} style={{animation:`efadeUp .5s ease ${i*.1}s both`}}><Card item={loc} isLoc={true}/></div>))}
            </div>
          </div>
        )}

        {/* bottom info */}
        <div style={{textAlign:"center", marginTop:40, display:"flex", alignItems:"center", justifyContent:"center", gap:20, flexWrap:"wrap"}}>
          {[{label:"All systems operational"},{label:"Auto-refresh every 15s"},{label:"AI real-time monitoring"}].map(item=>(
            <div key={item.label} style={{display:"flex", alignItems:"center", gap:7, background:BASE, padding:"7px 14px", borderRadius:20, boxShadow:inset}}>
              <div style={{width:6, height:6, borderRadius:"50%", background:"#0d9488"}}/>
              <span style={{fontSize:11, color:TSUB, fontFamily:"'JetBrains Mono',monospace", fontWeight:600}}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
